import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { SelectEntityId, setAllEntities, withEntities } from '@ngrx/signals/entities';

import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap } from 'rxjs';
import { UserService } from '../services/user.service';
import { IBalance } from '../components/models/balance.model';
import { compareAssetIds, formatAssetId } from '../utils/asset-id-utils';
import { AssetStore } from './asset.store';

type BalancesStoreState = {
  loaded: boolean;
  toggleSeeAllBalances: boolean;
};

const initialState: BalancesStoreState = {
  loaded: false,
  toggleSeeAllBalances: false,
};

const selectId: SelectEntityId<IBalance> = (balance) => balance.blockchainAsset.id;

export const BalancesStore = signalStore(
  {
    providedIn: 'root',
  },
  withEntities<IBalance>(),
  withState(initialState),
  withComputed(({ entities }, assetStore = inject(AssetStore)) => {
    const totalBalances = computed(() => {
      return entities()
        .filter((balance) => balance.amountNative > 0)
        .sort((a, b) => {
          a = formatAssetId(a);
          b = formatAssetId(b);
          return compareAssetIds(a, b);
        });
    });

    const enhancedTotalBalances = computed(() => {
      return totalBalances().map((balance) => {
        // Create a clone of the balance to avoid modifying the original
        const enhancedBalance = { ...balance };

        // Find the corresponding asset in the asset store by ticker
        const assetEntities = assetStore['entities']();
        const asset = Object.values(assetEntities).find(
          (asset) => asset.assetTicker === balance.blockchainAsset.assetTicker,
        );

        // Add assetName and isFiat to blockchainAsset if asset is found
        if (asset) {
          enhancedBalance.blockchainAsset = {
            ...enhancedBalance.blockchainAsset,
            assetName: asset.assetName,
            isFiat: asset.isFiat,
          };
        } else {
          // Provide default values if asset is not found
          enhancedBalance.blockchainAsset = {
            ...enhancedBalance.blockchainAsset,
            assetName: balance.blockchainAsset.assetTicker, // Use ticker as fallback name
            isFiat: false,
          };
        }

        return enhancedBalance;
      });
    });

    const getSortedBalances = computed(() => {
      return [...enhancedTotalBalances()]
        .filter((balance) => Number(balance.convertedAmount) > 0.01)
        .sort((a, b) => {
          return Number(b.convertedAmount) - Number(a.convertedAmount); // Sort in descending order
        });
    });

    return {
      totalBalances,
      enhancedTotalBalances,
      getSortedBalances,
    };
  }),
  withMethods((store, userService = inject(UserService)) => {
    const loadBalances = rxMethod<void>(
      pipe(
        switchMap(() => {
          return userService.getBalances().pipe(
            tapResponse({
              next: ({ total }) => {
                patchState(store, setAllEntities(total, { selectId }));
                patchState(store, { loaded: true });
              },
              error: (error: { message: string }) => {
                // todo: handle error
                console.log(error);
              },
            }),
          );
        }),
      ),
    );

    return {
      loadBalances,
    };
  }),
);
